import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from './lib/cn';
import { Badge } from './Badge';

const iconButton = cva(
  'relative inline-flex shrink-0 items-center justify-center rounded-md transition-colors duration-fast ease-emphasis focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        ghost: 'text-muted-foreground hover:bg-subtle hover:text-foreground active:bg-secondary',
        'ghost-brand': 'text-primary hover:bg-primary-subtle active:bg-brand-100',
        outline: 'border border-border bg-card text-secondary-foreground hover:bg-subtle',
        primary: 'bg-primary text-primary-foreground shadow-brand hover:bg-primary-hover',
        danger: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        /** On the meeting stage / dark popups. */
        stage: 'bg-white/10 text-white backdrop-blur-sm hover:bg-white/20',
        'stage-ghost': 'text-white/80 hover:bg-white/10 hover:text-white',
      },
      size: {
        xs: 'h-6 w-6',
        sm: 'h-7 w-7',
        md: 'h-8 w-8', //  32px — header "..." buttons, filter, gear
        lg: 'h-9 w-9', //  36px — top bar bell/help
        xl: 'h-10 w-10', // 40px — composer icons
      },
      round: { true: 'rounded-pill', false: '' },
    },
    defaultVariants: { variant: 'ghost', size: 'md', round: false },
  },
);

const GLYPH = { xs: 14, sm: 16, md: 16, lg: 20, xl: 20 } as const;

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>,
    VariantProps<typeof iconButton> {
  icon: LucideIcon;
  /** REQUIRED — icon-only buttons must be labelled for screen readers. */
  label: string;
  /** Overrides the size-derived glyph size. */
  iconSize?: number;
  /** Renders an unread counter at the top-right (bell, rail items). */
  badgeCount?: number;
  badgeTone?: 'danger' | 'brand';
  /** Active/selected state — e.g. a toggled toolbar control. */
  active?: boolean;
}

/** Square icon-only button. Always pass `label`. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    icon: Icon, label, iconSize, badgeCount, badgeTone = 'danger', active = false,
    variant, size = 'md', round, className, type = 'button', ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      aria-pressed={active || undefined}
      data-active={active || undefined}
      className={cn(
        iconButton({ variant, size, round }),
        active && 'bg-primary-subtle text-primary',
        className,
      )}
      {...props}
    >
      <Icon size={iconSize ?? GLYPH[size ?? 'md']} strokeWidth={2} aria-hidden />
      {badgeCount !== undefined && badgeCount > 0 ? (
        <Badge
          count={badgeCount}
          tone={badgeTone}
          size="sm"
          className="absolute -right-1 -top-1 ring-2 ring-card"
        />
      ) : null}
    </button>
  );
});
